"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Divider,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import { Delete, Email, Phone, Person, Message, Event } from "@mui/icons-material";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../../firebase";
import Swal from "sweetalert2";
import Menu from "../../panel/Menu";

function Webenquiry() {
  const auth = getAuth();

  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);

  /* ================= AUTH & PROPERTY ID FETCH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserData(user);

        try {
          // Fetch user propertyId from 'authroles'
          const roleSnap = await getDocs(collection(db, "authroles"));
          roleSnap.forEach((d) => {
            if (d.id === user.uid) {
              setPropertyId(d.data().propertyId);
            }
          });
        } catch (error) {
          console.error("Error fetching user roles:", error);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [auth]);

  /* ================= FETCH ENQUIRIES ================= */
  const fetchEnquiries = async () => {
    if (!userData || !propertyId) return;

    setLoading(true);
    try {
      // Query enquiries filtered by propertyId
      const q = query(
        collection(db, "contact_enquiries"),
        where("propertyId", "==", propertyId)
      );

      const querySnapshot = await getDocs(q);
      const list = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      // Sort by creation date (newest first)
      list.sort((a, b) => {
        const timeA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const timeB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return timeB - timeA;
      });

      setEnquiries(list);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData && propertyId) {
      fetchEnquiries();
    }
  }, [userData, propertyId]);

  /* ================= DELETE ENQUIRY ================= */
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this enquiry!",
      icon: "warning",
      showCancelButton: true,
      confirmColor: "#d33",
      cancelColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, "contact_enquiries", id));
        Swal.fire("Deleted!", "Enquiry has been removed.", "success");
        fetchEnquiries();
      } catch (error) {
        console.error("Error deleting enquiry:", error);
        Swal.fire("Error!", "Failed to delete enquiry.", "error");
      }
    }
  };

  // Helper function to format timestamp nicely
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return dateObj.toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      {/* Sidebar Menu Component */}
      <Menu />

      <Box flex={1} p={3} className="content-wrapper">
        <Typography variant="h5" fontWeight={700} mb={3}>
          Web Contact Enquiries
        </Typography>

        <Card sx={{ borderRadius: 3, boxShadow: "0 6px 20px rgba(0,0,0,0.08)" }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={5}>
                <CircularProgress />
              </Box>
            ) : enquiries.length === 0 ? (
              <Box textAlign="center" py={5}>
                <Typography color="textSecondary" variant="h6">
                  No web enquiries found for this property.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ backgroundColor: "#fafafa" }}>
                    <TableRow>
                      <TableCell fontweight={700}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person fontSize="small" color="action" /> Name
                        </Box>
                      </TableCell>
                      <TableCell fontweight={700}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Email fontSize="small" color="action" /> Contact Details
                        </Box>
                      </TableCell>
                      <TableCell fontweight={700}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Message fontSize="small" color="action" /> Message
                        </Box>
                      </TableCell>
                      <TableCell fontweight={700}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Event fontSize="small" color="action" /> Received On
                        </Box>
                      </TableCell>
                      <TableCell align="center" fontweight={700}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enquiries.map((row) => (
                      <TableRow key={row.id} hover>
                        {/* Guest Name */}
                        <TableCell>
                          <Typography fontWeight={600}>{row.name || "N/A"}</Typography>
                        </TableCell>

                        {/* Email & Phone */}
                        <TableCell>
                          <Typography variant="body2" display="block">
                            <a href={`mailto:${row.email}`} style={{ textDecoration: "none", color: "inherit" }}>
                              {row.email}
                            </a>
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block">
                            <a href={`tel:${row.phone}`} style={{ textDecoration: "none", color: "inherit" }}>
                              {row.phone}
                            </a>
                          </Typography>
                        </TableCell>

                        {/* Message Text */}
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {row.message}
                          </Typography>
                        </TableCell>

                        {/* Timestamp */}
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(row.createdAt)}
                          </Typography>
                        </TableCell>

                        {/* Action Buttons */}
                        <TableCell align="center">
                          <Tooltip title="Delete Enquiry">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(row.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Webenquiry;